import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiEye,
  FiHeart,
  FiMessageCircle,
  FiPlus,
  FiShare2,
  FiUser,
} from 'react-icons/fi';

import PageHeader from '../Components/PageHeader/PageHeader';
import Breadcrumb from '../Components/Breadcrumb/Breadcrumb';
import Button from '../Components/Button/Button';
import EmptyState from '../Components/EmptyState/EmptyState';
import AccountCard from '../Components/AccountCard/AccountCard';
import PromptModal from '../Components/PromptModal/PromptModal';
import ConfirmDialog from '../Components/ConfirmDialog/ConfirmDialog';
import RefreshButton from '../Components/RefreshButton/RefreshButton';
import StatBlock from '../Components/StatBlock/StatBlock';
import ContributionBreakdown from '../Components/ContributionBreakdown/ContributionBreakdown';

import {
  getAccountGroup,
  getAccountGroupStats,
  listAccounts,
  createAccount,
  deleteAccount,
} from '../Utilities/api';

import './accountGroupDetail.css';

function AccountGroupDetail() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [stats, setStats] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = useCallback(async () => {
    const [g, accts, st] = await Promise.all([
      getAccountGroup(groupId),
      listAccounts(groupId),
      getAccountGroupStats(groupId),
    ]);
    setGroup(g);
    setAccounts(accts);
    setStats(st);
  }, [groupId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async ({ name, description }) => {
    await createAccount({ groupId, name, description });
    await load();
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    await deleteAccount(pendingDelete.id);
    setPendingDelete(null);
    await load();
  };

  const breakdownRows = useMemo(() => {
    if (!stats?.accounts) return [];
    return [...stats.accounts]
      .sort((a, b) => (b.stats?.views || 0) - (a.stats?.views || 0))
      .map((a) => ({
        key: a.accountId,
        identity: <span>{a.name}</span>,
        meta: `${a.loginCount} ${a.loginCount === 1 ? 'platform' : 'platforms'} · ${a.uploadCount} ${a.uploadCount === 1 ? 'upload' : 'uploads'}`,
        stats: a.stats,
      }));
  }, [stats]);

  if (!group) return null;

  const totalStats = stats?.totals?.stats;
  const totalUploads = stats?.totals?.uploadCount ?? 0;

  return (
    <>
      <Breadcrumb
        items={[
          { label: 'Dashboard', to: '/dashboard' },
          { label: group.name },
        ]}
      />

      <PageHeader
        title={group.name}
        subtitle={group.description || 'Accounts in this group'}
        meta={
          <span>
            {accounts.length} {accounts.length === 1 ? 'account' : 'accounts'}
          </span>
        }
        actions={
          <>
            <RefreshButton level="group" id={group.id} onRefreshed={load} label="Refresh all" />
            <Button icon={<FiPlus />} onClick={() => setShowCreate(true)}>
              New account
            </Button>
          </>
        }
      />

      {accounts.length === 0 ? (
        <EmptyState
          icon={<FiUser />}
          title="No accounts yet"
          description="Create an account to represent a brand, persona, or show that spans one or more platforms."
          action={
            <Button icon={<FiPlus />} onClick={() => setShowCreate(true)}>
              Create account
            </Button>
          }
        />
      ) : (
        <>
          {totalStats && (
            <div className="group-detail-stats">
              <StatBlock label="Total views" value={totalStats.views} icon={<FiEye />} />
              <StatBlock label="Total likes" value={totalStats.likes} icon={<FiHeart />} />
              <StatBlock
                label="Total comments"
                value={totalStats.comments}
                icon={<FiMessageCircle />}
              />
              <StatBlock label="Total shares" value={totalStats.shares} icon={<FiShare2 />} />
            </div>
          )}

          {totalStats && totalUploads > 0 && (
            <ContributionBreakdown
              title="Contribution by account"
              totals={totalStats}
              rows={breakdownRows}
              onRowClick={(row) => navigate(`/accounts/${row.key}`)}
              emptyMessage="No uploads pulled on any account yet."
            />
          )}

          <h2 className="group-detail-section-title">Accounts</h2>

          <div className="group-detail-grid">
            {accounts.map((a) => (
              <AccountCard
                key={a.id}
                account={a}
                linkedPlatforms={a.linkedPlatforms || []}
                onDelete={(acct) => setPendingDelete(acct)}
                onRefreshed={load}
              />
            ))}
          </div>
        </>
      )}

      <PromptModal
        open={showCreate}
        title="Create account"
        submitLabel="Create account"
        nameLabel="Account name"
        namePlaceholder="e.g. Gaming Highlights"
        descriptionLabel="Description (optional)"
        descriptionPlaceholder="What does this account post?"
        onClose={() => setShowCreate(false)}
        onSubmit={handleCreate}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete account?"
        message={`This will permanently delete "${pendingDelete?.name}", unlink every platform, and remove all of its uploads. This cannot be undone.`}
        confirmLabel="Delete account"
        danger
        onClose={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}

export default AccountGroupDetail;
